/**
 * 조항 템플릿 2종 — 공정위 표준약관 제10079호 · 국토부 민간건설공사 표준도급계약서.
 *
 * **조항은 우리가 쓰지 않는다**(노트 `12`). 자작 문구는 ① 시공사에 유리하면 건산법
 * 부당특약으로 무효고 ② 고객에 유리하면 아무도 안 쓰고 ③ 사고 나면 우리 책임이다.
 * 인테리어 전용 공공 서식이 이미 있으므로 그것을 그대로 담는다.
 *
 * ## 본문은 원문이다
 *
 * 원본 텍스트가 `source/` 에 함께 있고(받은 URL·날짜가 파일 첫 줄에 있다),
 * `tests/contract-clauses-verbatim.test.ts` 가 **코드와 원문을 매번 대조**한다.
 * 한 글자라도 고치면 테스트가 깨진다 — 「표준약관 제10079호」 라는 표지가 거짓이
 * 되는 것을 사람의 주의력에 맡기지 않는다.
 *
 * `verbatim` 이 거짓인 템플릿은 **인쇄물이 출처 표지를 감춘다.** 우리가 손댄 문구에
 * 공공 서식의 이름을 붙이지 않기 위해서다.
 */

import { MOLIT_CLAUSE_ROWS } from "./data/molit-clauses";
import { ClauseSource, ContractClause } from "./types";

export { DEFAULT_CLAUSE_SOURCE } from "./defaults";

export interface ClauseTemplate {
  source: Exclude<ClauseSource, "CUSTOM">;
  name: string;
  /** 인쇄물 하단에 그대로 찍는 출처. `verbatim` 이 참일 때만 보인다. */
  attribution: string;
  /**
   * 템플릿 판. **스냅샷이 `canonical` 에 같이 굳힌다**(노트 `12`) — 「그때 어떤
   * 템플릿이었나」 를 못 되짚으면 나중에 원문으로 갈아 끼웠을 때 `isEditedFrom`
   * 이 옛 계약을 「고쳤다」 로 잘못 읽는다. 본문을 한 글자라도 고치면 올린다.
   */
  version: string;
  /**
   * 본문이 공식 원문 그대로인가. **false 면 출처 표지를 찍지 않는다.**
   * 우리가 손댄 문구에 공공 서식의 이름을 붙이지 않기 위해서다.
   */
  verbatim: boolean;
  clauses: readonly ContractClause[];
}

const clausesOf = (rows: readonly [string, string][]): ContractClause[] =>
  rows.map(([title, body], i) => ({ no: i + 1, title, body }));

/**
 * 공정거래위원회 표준약관 제10079호 「실내건축·창호 공사 표준계약서」.
 * 소비자를 상대하는 인테리어 전용 서식이라 **기본값**이다.
 */
export const FTC_10079_TEMPLATE: ClauseTemplate = {
  source: "FTC_10079",
  name: "실내건축·창호 공사 표준계약서",
  attribution: "공정거래위원회 표준약관 제10079호 (2018. 3. 21. 제정)",
  version: "10079-2018-03-21",
  verbatim: true,
  clauses: clausesOf([
    [
      "목적",
      "이 계약서는 실내건축·창호 공사를 의뢰한 소비자와 시공업자와 사이에 체결된 공사 계약상의 권리·의무 및 책임에 관한 사항을 규정함을 목적으로 한다.",
    ],
    [
      "계약서 제공·설명 의무",
      "“시공업자”는 계약체결 시 소비자에게 상호 및 대표자 성명, 영업소재지 주소(“소비자”의 불만을 처리할 수 있는 곳의 주소 포함)를 기재한 본 계약서, 공사면허 등을 소비자에게 제공하고 다음 각 호의 규정을 “소비자”가 이해할 수 있도록 설명하여야 한다.\n  1. 시공장소 및 공사일정\n  2. 공사비(계약금, 중도금, 잔금) 및 지급방법\n  3. 공사의 범위 및 공사의 내역\n  4. 연체료 및 지체보상금\n  5. 계약보증 및 해제, 위약금\n  6. 공사의 변경, 양도양수, 하자보수",
    ],
    [
      "계약내용",
      "① 시공장소 :\n② 공사일정 : 착공일     .     .     부터 공사완료일     .     .     (     일간)\n  ※ 단, 아파트 입주예정일 지연 등 부득이한 사정이 발생한 경우 “소비자”와 “시공업자”는 합의하여 공사 완공일자를 조정 할 수 있다.\n③ 총 공사금액 :                    (부가가치세 포함)\n  구  분      계약금      중도금      잔 금\n  금  액\n  지급일\n  ※ 납부계좌번호 :\n④ “시공업자”는 제3조제3항의 공사금액을 원자재 가격 상승 등을 이유로 인상할 수 없다.\n⑤ 공사의 범위 및 공사의 내역 : “시공업자”는 “소비자”가 쉽게 이해할 수 있도록 소비자에게 공사의 범위와 물량, 시공자재의 제품, 규격 등을 기재한 별도의 내역서를 제출하여야 한다.\n⑥ 창호 공사의 경우에는 시공자재의 제품명(제조사), 제품색상, 유리두께, 유리색상 등을 구체적으로 기재한다.",
    ],
    [
      "“시공업자”의 의무",
      "① “시공업자”는 제3조의 계약내용을 준수하여 공사를 완료하여야 한다.\n② “시공업자”는 공사완료 후 당초 설계서에 의한 공사내용이 계약내용과 이상이 없음을 “소비자”에게 확인시켜야 한다.\n③ “시공업자”는 제10조 규정에 따라 하자보수의 책임을 진다.",
    ],
    [
      "“소비자”의 의무",
      "① “소비자”는 제3조 제3항의 공사금액을 정해진 기일에 “시공업자”에게 지급하여야 한다.\n② “소비자”는 공사금액을 지급함에 있어 하자가 발견되었을 경우 “소비자”는 하자의 보수나 하자보수에 갈음하는 금액을 “시공업자”에게 청구할 수 있으며, 이를 청구한 경우 “소비자”는 “시공업자”가 하자를 보수하거나 하자보수에 갈음하는 금액을 지급 할 때까지 그에 상응하는 공사금액의 지급을 거절할 수 있다.",
    ],
    [
      "지연배상",
      "① “소비자”가 공사금액의 지급을 지연한 경우 “소비자”는 연체일로부터 실제 지급일까지 (     )%의 연체이율을 적용한 지연손해금을 “시공업자”에게 지급하여야 한다. 다만, 연체이율은 시중은행 일반자금 대출의 연체이율 범위내에서 정하도록 한다.\n② “시공업자”가 계약서에서 정한 공사완료 일자를 준수하지 않을 경우 “시공업자”는 “소비자”가 공사완료 이전까지 지급한 금액에 대하여 공사지연일로부터 최종 공사완공일까지 기간에 전항의 연체이율을 적용한 지연손해금을 “소비자”에게 지급하여야 한다.",
    ],
    [
      "계약해제 및 위약금",
      "① “소비자” 또는 “시공업자”는 다음 각 호의 어느 하나에 해당하는 경우 상대방에게 서면으로 즉시 계약을 해제할 수 있다.\n  1. “시공업자”의 책임 있는 사유로 공사완료일 내에 공사를 완성할 가능성이 없음이 명백한 경우\n  2. “소비자” 또는 “시공업자”가 계약조건을 위반하여 그 위반으로 계약의 목적을 달성할 수 없을 경우\n② “소비자”는 “시공업자”가 정당한 사유 없이 착공을 지연한 경우 상당한 기간을 정하여 서면으로 계약의 이행을 최고한 후 동 기간 내에 계약이 이행되지 아니한 때 계약을 해제할 수 있다.\n③ “소비자” 또는 “시공업자”의 귀책사유로 계약이 해제된 경우 “소비자” 또는 “시공업자”는 다음 각 호의 규정에 의한 위약금을 상대방에게 지급하여야 한다.\n  1. 계약 또는 계약 후 실측만 한 경우 : 총 공사금액의 (     )%(단 총 공사금액의 10%를 초과할 수 없음)\n  2. 제작 또는 공사에 착수한 경우 : 실 손해액 배상",
    ],
    [
      "공사변경",
      "① “시공업자”는 공사의 설계 및 자재변경 등으로 인하여 계약한 제품의 공급이 불가능할 경우 변경 시공할 내역을 “소비자”에게 통보하고, “소비자”와 협의한 후 동질·동가의 제품으로 시공할 수 있다. 다만, 이를 이유로 제3조 3항의 공사금액을 인상할 수 없다\n② “소비자”의 사정에 의하여 공사내용이 변경되는 경우 “소비자”와 “시공업자”는 협의하여 변경할 수 있고, 공사내용의 변경으로 발생하는 추가비용은 “소비자”가 지급한다.",
    ],
    [
      "양도양수",
      "① “소비자”는 매매 등 계약의 이행에 영향을 미치는 사유가 발생하였을 때에는 “시공업자”에게 서면으로 통지하여야 한다.\n② “소비자”의 건축물에 대한 매매 등 소유권 이전이 발생한 경우 “소비자”와 “시공업자”는 본 계약이 소유권을 이전받는 자에게 승계되도록 노력하여야 한다. 다만, 본 계약이 승계되지 아니한 경우 “소비자” 또는 “시공업자”는 계약을 해제할 수 있고 “소비자”는 제7조 제2항에 따른 위약금을 “시공업자”에게 지급 하여야 한다.",
    ],
    [
      "하자보수",
      "① “시공업자”는 공사완료 후, 균열, 누수, 파손 등의 하자가 발생하였을 때 다음 각 호에 해당하는 기간(이하 ‘무상 수리기간’)에는 무상 수리를 해주어야 한다. 다만 무상 수리기간 중 “소비자”의 사용상 부주의로 하자가 발생하여 “소비자”가 “시공업자”에게 수리를 청구한 경우, “시공업자”는 수리에 응하되 그 비용은 “소비자”가 부담한다.\n  1. 실내건축 공사 : 공사 종료 후 1년 이내\n  2. 창호 공사 : 공사 종료 후 2년 이내(유리는 1년)\n② “시공업자”는 무상 수리기간이 지난 후 발생한 하자에 대하여 “소비자”가 수리를 요청하는 경우 “소비자”의 비용 부담으로 유상수리 할 수 있다.\n③ “시공업자”가 공사에 사용한 제품이 계약서상의 규격에 미달할 경우 “소비자”는 “시공업자”에게 교체시공이나 공사금액 차액 환급 등의 손해배상을 “시공업자”에게 청구할 수 있다.",
    ],
    [
      "분쟁의 해결",
      "이 계약서에서 규정하지 않은 사항과 해석에 관하여는 건설산업기본법, 약관의 규제 등에 관한 법률, 공정거래위원회가 정하는 소비자 보호지침 및 관계법령 또는 상관례에 따른다.",
    ],
    [
      "관할법원",
      "이 계약과 관련된 분쟁에 관한 소송은 민사소송법상의 관할법원에 제기하여야 한다.",
    ],
  ]),
};

/**
 * 원문에 함께 실린 소비자 유의사항. **조항이 아니므로 `clauses` 에 넣지 않는다** —
 * 조 번호가 붙으면 제13조가 되어 원문과 달라진다. 인쇄물이 조항 뒤에 따로 싣는다.
 */
export const FTC_10079_CONSUMER_NOTICE =
  "공사예정금액이 1,500만원 이상인 경우에는 사업자가「건설산업기본법」에 따라 전문건설업에 등록한 업체인지 확인(건설업등록증 및 건설업수첩)하는 것이 좋습니다.";

// 제9조 ② 의 「제7조 제2항에 따른 위약금」 은 공정위 원문 자체의 오기(위약금은 제7조 ③) — 원문은 고치지 않고 각주만 단다.
export const FTC_ART9_NO = 9;
const FTC_ART9_LINE = FTC_10079_TEMPLATE.clauses[FTC_ART9_NO - 1]?.body
  .split("\n")
  .find((line) => line.startsWith("②") && line.includes("제7조 제2항에 따른 위약금"));
if (FTC_ART9_LINE === undefined) {
  throw new Error("공정위 원문 제9조 ② 줄을 찾지 못했습니다 — 원문 판을 확인하세요");
}
const FTC_ART7_NO = 7;
const FTC_ART7_PENALTY_LINE = FTC_10079_TEMPLATE.clauses[FTC_ART7_NO - 1]?.body
  .split("\n")
  .find((line) => line.startsWith("③") && line.includes("위약금을 상대방에게 지급"));
if (FTC_ART7_PENALTY_LINE === undefined) {
  throw new Error("공정위 원문 제7조 ③ 줄을 찾지 못했습니다 — 원문 판을 확인하세요");
}

export const FTC_ART9_CITATION_NOTE =
  "※ 제9조 ②의 「제7조 제2항」은 공정위 표준약관 원문 표기 그대로이며, 위약금 조항은 제7조 ③입니다.";

export const FTC_ART9_CITATION_HINT = "제9조 ②의 「제7조 제2항」은 원문 표기 그대로입니다. 위약금은 제7조 ③에 있습니다.";

// 제9조 ② 줄과 제7조 ③(위약금) 줄이 둘 다 원문 그대로인가 — 하나라도 고치면 각주가 틀린 말이 된다.
const keepsLine = (clauses: readonly ContractClause[], no: number, line: string): boolean =>
  clauses.some((c) => c.no === no && c.body.split("\n").includes(line));

export const hasFtcArt9Citation = (clauses: readonly ContractClause[]): boolean =>
  keepsLine(clauses, FTC_ART9_NO, FTC_ART9_LINE) && keepsLine(clauses, FTC_ART7_NO, FTC_ART7_PENALTY_LINE);

/**
 * 국토교통부 「민간건설공사 표준도급계약서」(고시 제2026-34호) 붙임 **일반조건**
 * 43개조. 금액이 큰 현장용 선택지다.
 *
 * **본문은 생성 파일에서 온다**(`data/molit-clauses.ts`) — 43개조 45KB 를 손으로
 * 옮기면 오타가 섞이고 섞인 줄을 찾을 길이 없다. 원문과 같은지는
 * `tests/contract-clauses-verbatim.test.ts` 가 매번 대조한다.
 */
export const MOLIT_TEMPLATE: ClauseTemplate = {
  source: "MOLIT",
  name: "민간건설공사 표준도급계약 일반조건",
  attribution: "국토교통부 고시 제2026-34호 (2026. 1. 16. 시행)",
  version: "molit-2026-34",
  verbatim: true,
  clauses: MOLIT_CLAUSE_ROWS.map((row, i) => ({
    no: i + 1,
    label: `제${row.label}`,
    title: row.title,
    body: row.body,
  })),
};

export const CLAUSE_TEMPLATES: Record<
  Exclude<ClauseSource, "CUSTOM">,
  ClauseTemplate
> = {
  FTC_10079: FTC_10079_TEMPLATE,
  MOLIT: MOLIT_TEMPLATE,
};

/**
 * 그 출처의 기본 조항. `CUSTOM` 은 **템플릿이 없다** — 회사가 고친 사본이
 * 원본이므로 여기서 되돌릴 것이 없다.
 */
export const defaultClauses = (source: ClauseSource): ContractClause[] =>
  source === "CUSTOM"
    ? []
    : CLAUSE_TEMPLATES[source].clauses.map((c) => ({ ...c }));

/**
 * 조항이 템플릿과 한 글자라도 다른가. **다르면 사본만 `CUSTOM`** 이 되고
 * 「공정위 표준」 표지를 뗀다(노트 `12` 열린 질문 D).
 */
export const isEditedFrom = (
  source: Exclude<ClauseSource, "CUSTOM">,
  clauses: readonly ContractClause[]
): boolean => {
  const base = CLAUSE_TEMPLATES[source].clauses;
  if (base.length !== clauses.length) return true;
  return base.some(
    (b, i) =>
      b.no !== clauses[i].no ||
      // `undefined` 와 `null` 을 같게 본다 — 저장소를 한 번 다녀온 조항은 빠진
      // 키가 `null` 로 돌아오므로, 그냥 비교하면 **손 안 댄 계약이 CUSTOM 으로
      // 강등**된다(표지가 조용히 떨어진다).
      (b.label ?? null) !== (clauses[i].label ?? null) ||
      b.title !== clauses[i].title ||
      b.body !== clauses[i].body
  );
};

/** 편집 결과의 출처. 원본과 같으면 출처를 지키고, 다르면 `CUSTOM` 으로 떨어진다. */
export const clauseSourceAfterEdit = (
  source: ClauseSource,
  clauses: readonly ContractClause[]
): ClauseSource =>
  source === "CUSTOM" || isEditedFrom(source, clauses) ? "CUSTOM" : source;

// 설정 화면·인쇄가 같이 쓰는 판정 — 고른 서식 원문과 한 글자라도 다르면 CUSTOM, 빈 목록은 원문 그대로.
export const judgeClauseSource = (
  base: Exclude<ClauseSource, "CUSTOM">,
  clauses: readonly ContractClause[]
): ClauseSource =>
  clauses.length === 0 || !isEditedFrom(base, clauses) ? base : "CUSTOM";

// 인쇄물 하단 출처 표지. CUSTOM 이거나 축자 원문이 아니면 찍지 않는다.
export const clauseAttribution = (source: ClauseSource): string | null => {
  if (source === "CUSTOM") return null;
  const template = CLAUSE_TEMPLATES[source];
  return template.verbatim ? template.attribution : null;
};

// 화면 표지. 표지가 없는 출처(CUSTOM)는 null.
export const clauseBadge = (source: ClauseSource): string | null =>
  clauseAttribution(source) === null
    ? null
    : source === "FTC_10079"
      ? "공정위 표준"
      : "국토부 표준";
